import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MasterCommonTable from "../../UiComponents/CommonTable/MasterCommonTable";
import { SearchIcon } from "../../Resources/Icons/icons";
import walletIcon from "../../Resources/Images/wallet.png";
import { GetPolicyWalletSummary } from "../../Service/_index";
import { formatINR } from "../AddMoneyToUser/addMoneyUtils";
import { outlinedButtonSx } from "../AddMoneyToUser/parts";
import WalletStatementDialog from "./WalletStatementDialog";
import { walletWarnings } from "./walletStatementUtils";

const tableConfig = {
  isAction: false,
  isView: false,
  isEdit: false,
  isDelete: false,
  isSearch: false,
  isUpload: false,
  isFileDownload: false,
};

const AmountTile = ({ value, label, hint, color = "#0070FF" }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      px: "15px",
      py: "4px",
      backgroundColor: "#F0F5FA",
      border: "1px solid #DFE7F1",
      borderRadius: "8px",
    }}
  >
    <img src={walletIcon} width="18" height="17" alt="" />
    <Box>
      <Typography sx={{ fontFamily: "Inter", fontWeight: 500, color, fontSize: 16, lineHeight: 1.2 }}>
        {formatINR(value)}
      </Typography>
      <Typography sx={{ fontFamily: "Inter", fontWeight: 500, color: "#667085", fontSize: 12, lineHeight: 1.2 }}>
        {label}
        {hint ? ` · ${hint}` : ""}
      </Typography>
    </Box>
  </Box>
);

// Loads fresh every time it is shown (the page unmounts it on the other tab), so balances are never stale.
const UserWalletsTab = ({ onSummary, onOpenPolicy }) => {
  const [users, setUsers] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [statement, setStatement] = useState({ open: false, user: null });
  const latestRequest = useRef(0);

  useEffect(() => {
    const id = ++latestRequest.current;
    setLoading(true);
    setError("");
    GetPolicyWalletSummary().then((res) => {
      if (id !== latestRequest.current) return; // a newer request superseded this one
      setLoading(false);
      if (res.error) {
        setError(res.message || "Could not load the user wallets");
        return;
      }
      const body = res.data || {};
      setUsers(Array.isArray(body.users) ? body.users : []);
      setTotals(body.totals || null);
      setHasLoaded(true);
    });
    return () => {
      latestRequest.current += 1;
    };
  }, [reloadKey]);

  // S.No counts the rows on screen. Sortable columns keep their raw numbers.
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users
      .filter(
        (user) =>
          !term ||
          [user.userName, user.mobileNumber, user.email, user.adminName].some((value) =>
            String(value || "")
              .toLowerCase()
              .includes(term),
          ),
      )
      .map((user, index) => ({ ...user, __index__: index + 1 }));
  }, [users, search]);

  // Only the super admin's view carries adminName (every admin's users together); an admin sees just their own.
  const showAdmin = users.some((user) => user.adminName !== undefined);

  const columns = [
    { id: "__index__", placeMent: true, disablePadding: false, label: "S.No", type: "index" },
    {
      id: "userName",
      placeMent: true,
      disablePadding: false,
      label: "User",
      type: "component",
      renderComponent: (row) => {
        const warnings = walletWarnings(row);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 500, fontFamily: "Inter" }}>{row.userName || "—"}</Typography>
              {(row.mobileNumber || row.email) && (
                <Typography sx={{ fontSize: "12px", color: "#667085", fontFamily: "Inter" }}>
                  {row.mobileNumber || row.email}
                </Typography>
              )}
            </Box>
            {!row.isEnabled && (
              <Chip
                size="small"
                label="Disabled"
                sx={{ height: 20, fontSize: "11px", color: "#344054", backgroundColor: "#F2F4F7" }}
              />
            )}
            {warnings.length > 0 && (
              <Tooltip
                arrow
                title={
                  <Box sx={{ fontFamily: "Inter", fontSize: "12px", maxWidth: 280 }}>
                    {warnings.map((text) => (
                      <div key={text}>{text}</div>
                    ))}
                  </Box>
                }
              >
                <IconButton
                  size="small"
                  aria-label={`Check ${row.userName || "this"} wallet: ${warnings.join(" ")}`}
                  sx={{ color: "#B54708" }}
                >
                  <WarningAmberIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    ...(showAdmin
      ? [
          {
            id: "adminName",
            placeMent: true,
            disablePadding: false,
            label: "Admin",
            type: "component",
            renderComponent: (row) => (
              <Typography sx={{ fontFamily: "Inter" }}>{row.adminName || "—"}</Typography>
            ),
          },
        ]
      : []),
    {
      id: "balance",
      placeMent: true,
      disablePadding: false,
      label: "Balance",
      type: "component",
      renderComponent: (row) => (
        <Typography
          sx={{ fontWeight: 600, color: row.balance < 0 ? "#B42318" : "#1E3A5F", fontVariantNumeric: "tabular-nums" }}
        >
          {formatINR(row.balance)}
        </Typography>
      ),
    },
    {
      id: "pendingAmount",
      placeMent: true,
      disablePadding: false,
      label: "Pending",
      type: "component",
      renderComponent: (row) => (
        <Box>
          <Typography
            sx={{
              fontWeight: 500,
              color: row.pendingAmount > 0 ? "#B54708" : "#98A2B3",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatINR(row.pendingAmount)}
          </Typography>
          {row.pendingCount > 0 && (
            <Typography sx={{ fontSize: "12px", color: "#667085" }}>
              {row.pendingCount} request{row.pendingCount === 1 ? "" : "s"}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: "totalAdded",
      placeMent: true,
      disablePadding: false,
      label: "Total Added",
      type: "component",
      renderComponent: (row) => (
        <Typography sx={{ fontVariantNumeric: "tabular-nums" }}>{formatINR(row.totalAdded)}</Typography>
      ),
    },
    {
      id: "totalUsed",
      placeMent: true,
      disablePadding: false,
      label: "Total Used",
      type: "component",
      renderComponent: (row) => (
        <Typography sx={{ fontVariantNumeric: "tabular-nums" }}>{formatINR(row.totalUsed)}</Typography>
      ),
    },
    {
      id: "action",
      placeMent: false,
      disablePadding: false,
      label: "Action",
      type: "component",
      renderComponent: (row) => (
        <ButtonGroup size="small" variant="contained" sx={{ gap: 1 }}>
          <Button
            onClick={() => setStatement({ open: true, user: { userId: row.userId, userName: row.userName } })}
            sx={{ backgroundColor: "#1E3A5F", "&:hover": { backgroundColor: "#2E5A8F" } }}
          >
            Statement
          </Button>
          <Button
            onClick={() => onSummary && onSummary({ id: row.userId, name: row.userName || "Unknown user" })}
            sx={{ backgroundColor: "#D4AF37", color: "#1E3A5F", "&:hover": { backgroundColor: "#C9A961" } }}
          >
            Summary
          </Button>
        </ButtonGroup>
      ),
    },
  ];

  return (
    <>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        columnSpacing={2}
        className="Master_Card_Sub_Container"
      >
        <Grid item xs={12} md="auto" sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <AmountTile value={totals ? totals.balance : 0} label="Total Wallet" />
          <AmountTile
            value={totals ? totals.pendingAmount : 0}
            label="Total Pending"
            hint={
              totals && totals.pendingCount
                ? `${totals.pendingCount} request${totals.pendingCount === 1 ? "" : "s"}`
                : ""
            }
            color="#B54708"
          />
        </Grid>
        <Grid item xs={12} sm={5} md={3.5} mt={{ xs: 2, md: 0 }}>
          <Input
            focused="false"
            className="w-100 Master_Card_Input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            disableUnderline={true}
            placeholder="Search user"
            inputProps={{ "aria-label": "Search user" }}
            startAdornment={
              <InputAdornment position="start" sx={{ marginRight: "0px" }}>
                <IconButton aria-label="search" tabIndex={-1}>
                  <SearchIcon sx={{ fontSize: "20px" }} />
                </IconButton>
              </InputAdornment>
            }
          />
        </Grid>
      </Grid>

      {totals && totals.flaggedUsers > 0 && (
        <Grid item xs={12} sx={{ px: 2, pb: 1 }}>
          <Box
            role="alert"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              p: 1.25,
              border: "1px solid #FEDF89",
              borderRadius: "8px",
              backgroundColor: "#FFFAEB",
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 20, color: "#B54708" }} />
            <Typography sx={{ fontFamily: "Inter", fontSize: "12.5px", color: "#93370D" }}>
              {totals.flaggedUsers === 1 ? "1 wallet does not add up" : `${totals.flaggedUsers} wallets do not add up`}.
              Look for the warning mark next to the user name.
            </Typography>
          </Box>
        </Grid>
      )}

      {error && (
        <Grid item xs={12} sx={{ px: 2, pb: 2 }}>
          <Box
            role="alert"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              border: "1px solid #f3d6d6",
              borderRadius: "8px",
              backgroundColor: "#fdf7f7",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 20, color: "#b3261e" }} />
            <Typography sx={{ flex: 1, fontFamily: "Inter", fontSize: "12.5px", color: "#b3261e" }}>{error}</Typography>
            <Button size="small" variant="outlined" onClick={() => setReloadKey((n) => n + 1)} sx={outlinedButtonSx}>
              Retry
            </Button>
          </Box>
        </Grid>
      )}

      {!error && (
        <Grid item xs={12}>
          <MasterCommonTable
            tableConfig={tableConfig}
            data={rows}
            columns={columns}
            loading={loading && !hasLoaded}
            paginationReset={search}
          />
        </Grid>
      )}

      <WalletStatementDialog
        open={statement.open}
        user={statement.user}
        onClose={() => setStatement((prev) => ({ ...prev, open: false }))}
        onOpenPolicy={onOpenPolicy}
      />
    </>
  );
};

export default UserWalletsTab;
